<template>
  <div
    class="floating-panel"
    :class="{ minimized: isMinimized }"
    :style="panelStyle"
    @mousedown="startDrag"
  >
    <!-- 标题栏 -->
    <div class="panel-header">
      <span class="panel-title">📈 交易下单</span>
      <div class="panel-controls">
        <button class="control-btn" @click.stop="toggleMinimize" :title="isMinimized ? '展开' : '最小化'">
          {{ isMinimized ? '□' : '−' }}
        </button>
        <button class="control-btn close-btn" @click.stop="closePanel" title="关闭">×</button>
      </div>
    </div>

    <!-- 内容区域 -->
    <div class="panel-content" v-show="!isMinimized">
      <form @submit.prevent="handleSubmit" class="trade-form">
        <!-- 合约代码（只读显示） -->
        <div class="form-row">
          <label class="form-label">合约代码</label>
          <input
            :value="symbol"
            type="text"
            class="form-input"
            readonly
            style="background: rgba(35, 38, 48, 0.8); color: #8a8d97;"
          />
        </div>

        <!-- 订单类型 -->
        <div class="form-row">
          <label class="form-label">订单类型</label>
          <select v-model="orderType" class="form-select">
            <option value="market">市价单</option>
            <option value="limit">限价单</option>
          </select>
        </div>

        <!-- 每价位盈亏 -->
        <div class="form-row">
          <label class="form-label">每价位</label>
          <input
            v-model.number="pricePerTick"
            type="number"
            step="1"
            min="1"
            class="form-input"
            title="每跳动一个价位的盈亏金额"
          />
          <span class="form-unit">元/点位</span>
        </div>

        <!-- 手数 -->
        <div class="form-row">
          <label class="form-label">手数</label>
          <input
            v-model.number="quantity"
            type="number"
            min="1"
            max="100"
            class="form-input"
          />
        </div>

        <!-- 限价（仅限价单显示） -->
        <div class="form-row" v-if="orderType === 'limit'">
          <label class="form-label">限价</label>
          <input
            v-model.number="price"
            type="number"
            step="0.5"
            class="form-input"
          />
        </div>

        <!-- 止损价 -->
        <div class="form-row">
          <label class="form-label">止损价</label>
          <input
            v-model.number="stopLossPrice"
            type="number"
            step="0.5"
            class="form-input"
          />
        </div>

        <!-- 操作按钮 -->
        <div class="form-actions">
          <button type="button" class="trade-btn buy-btn" @click="handleBuy">
            做多
          </button>
          <button type="button" class="trade-btn sell-btn" @click="handleSell">
            做空
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  symbol: {
    type: String,
    default: 'JM'
  }
})

const emit = defineEmits(['submit', 'close'])

// 面板状态
const isMinimized = ref(false)
const isDragging = ref(false)

// 面板位置
const position = ref({ x: 20, y: 20 })
const dragOffset = ref({ x: 0, y: 0 })

// 表单数据
const orderType = ref('market')
const price = ref(0)
const quantity = ref(1)
const stopLossPrice = ref(0)
const pricePerTick = ref(25)

// 面板样式
const panelStyle = computed(() => ({
  left: `${position.value.x}px`,
  top: `${position.value.y}px`,
  cursor: isDragging.value ? 'move' : 'default'
}))

// 加载保存的配置和位置
onMounted(() => {
  // 加载保存的配置
  const savedConfig = localStorage.getItem('tradeConfig')
  if (savedConfig) {
    try {
      const config = JSON.parse(savedConfig)
      pricePerTick.value = config.pricePerTick || 25
    } catch (e) {
    }
  }

  // 加载保存的位置
  const savedPosition = localStorage.getItem('floatingTradePanel_position')
  if (savedPosition) {
    try {
      const parsed = JSON.parse(savedPosition)
      // 确保位置在屏幕范围内
      const maxX = window.innerWidth - 300
      const maxY = window.innerHeight - 100
      position.value = {
        x: Math.min(Math.max(0, parsed.x), maxX),
        y: Math.min(Math.max(0, parsed.y), maxY)
      }
    } catch (e) {
    }
  }
  
  // 添加全局鼠标事件监听
  document.addEventListener('mousemove', onDrag)
  document.addEventListener('mouseup', stopDrag)
})

// 保存配置和位置
onBeforeUnmount(() => {
  // 保存配置
  const config = {
    pricePerTick: pricePerTick.value
  }
  localStorage.setItem('tradeConfig', JSON.stringify(config))
  
  // 保存位置
  localStorage.setItem('floatingTradePanel_position', JSON.stringify(position.value))
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
})

// 开始拖拽
function startDrag(event) {
  if (event.target.closest('.panel-controls') || 
      event.target.closest('.form-select') ||
      event.target.closest('.form-input') ||
      event.target.closest('.trade-btn')) {
    return
  }
  
  isDragging.value = true
  dragOffset.value = {
    x: event.clientX - position.value.x,
    y: event.clientY - position.value.y
  }
}

// 拖拽中
function onDrag(event) {
  if (!isDragging.value) return
  
  const newX = event.clientX - dragOffset.value.x
  const newY = event.clientY - dragOffset.value.y
  
  // 限制在屏幕范围内
  const maxX = window.innerWidth - 300
  const maxY = window.innerHeight - (isMinimized.value ? 40 : 300)
  
  position.value = {
    x: Math.min(Math.max(0, newX), maxX),
    y: Math.min(Math.max(0, newY), maxY)
  }
}

// 停止拖拽
function stopDrag() {
  if (isDragging.value) {
    isDragging.value = false
    // 保存位置
    localStorage.setItem('floatingTradePanel_position', JSON.stringify(position.value))
  }
}

// 切换最小化
function toggleMinimize() {
  isMinimized.value = !isMinimized.value
}

// 关闭面板
function closePanel() {
  emit('close')
}

// 保存配置到 localStorage
function saveConfig() {
  const config = {
    pricePerTick: pricePerTick.value
  }
  localStorage.setItem('tradeConfig', JSON.stringify(config))
}

// 做多
function handleBuy() {
  saveConfig() // 保存配置
  const orderData = {
    symbol: props.symbol,
    side: 'buy',
    order_type: orderType.value,
    quantity: quantity.value,
    price: orderType.value === 'limit' ? price.value : 0,
    stop_loss_price: stopLossPrice.value,
    price_per_tick: pricePerTick.value
  }
  emit('submit', orderData)
}

// 做空
function handleSell() {
  saveConfig() // 保存配置
  const orderData = {
    symbol: props.symbol,
    side: 'sell',
    order_type: orderType.value,
    quantity: quantity.value,
    price: orderType.value === 'limit' ? price.value : 0,
    stop_loss_price: stopLossPrice.value,
    price_per_tick: pricePerTick.value
  }
  emit('submit', orderData)
}

function handleSubmit() {
  // 由点击事件处理
}
</script>

<style scoped>
.floating-panel {
  position: fixed;
  z-index: 9999;
  background: rgba(19, 23, 34, 0.95);
  border: 1px solid #2a2e39;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  min-width: 200px;
  transition: box-shadow 0.2s;
  backdrop-filter: blur(10px);
}

.floating-panel:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.6);
}

.floating-panel.minimized {
  min-width: 180px;
}

/* 标题栏 */
.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: rgba(42, 46, 57, 0.8);
  border-radius: 8px 8px 0 0;
  cursor: move;
  user-select: none;
}

.panel-title {
  font-size: 12px;
  font-weight: 600;
  color: #d1d4dc;
}

.panel-controls {
  display: flex;
  gap: 4px;
}

.control-btn {
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  color: #787b86;
  font-size: 14px;
  cursor: pointer;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.control-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #d1d4dc;
}

.close-btn:hover {
  background: rgba(242, 54, 69, 0.2);
  color: #f23645;
}

/* 内容区域 */
.panel-content {
  padding: 12px;
}

/* 表单样式 */
.trade-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-label {
  flex: 0 0 60px;
  font-size: 11px;
  color: #787b86;
  text-align: right;
}

.form-select,
.form-input {
  flex: 1;
  padding: 6px 8px;
  background: rgba(25, 28, 38, 0.8);
  border: 1px solid #2a2e39;
  border-radius: 4px;
  color: #d1d4dc;
  font-size: 11px;
  outline: none;
  transition: all 0.2s;
}

.form-select:focus,
.form-input:focus {
  border-color: #2962ff;
  background: rgba(30, 33, 43, 0.9);
}

.form-select:hover,
.form-input:hover {
  border-color: #363a45;
}

/* 单位样式 */
.form-unit {
  flex: 0 0 auto;
  font-size: 10px;
  color: #787b86;
  white-space: nowrap;
}

/* 下拉选项 */
.form-select option {
  background: #131722;
  color: #d1d4dc;
}

/* 数字输入框 */
.form-input[type="number"] {
  -moz-appearance: textfield;
}

.form-input[type="number"]::-webkit-inner-spin-button,
.form-input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* 操作按钮 */
.form-actions {
  display: flex;
  gap: 8px;
  margin-top: 4px;
  position: relative;
  z-index: 10000;
}

.trade-btn {
  flex: 1;
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  color: #ffffff;
  text-align: center;
  position: relative;
  z-index: 10001;
  pointer-events: auto !important;
}

/* 做多按钮 - 绿色 */
.buy-btn {
  background: #089981;
  box-shadow: 0 2px 4px rgba(8, 153, 129, 0.3);
}

.buy-btn:hover {
  background: #0ab893;
  filter: brightness(1.1);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(8, 153, 129, 0.4);
}

.buy-btn:active {
  background: #007a63;
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(8, 153, 129, 0.3);
}

/* 做空按钮 - 红色 */
.sell-btn {
  background: #f23645;
  box-shadow: 0 2px 4px rgba(242, 54, 69, 0.3);
}

.sell-btn:hover {
  background: #ff4d5a;
  filter: brightness(1.1);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(242, 54, 69, 0.4);
}

.sell-btn:active {
  background: #c92a38;
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(242, 54, 69, 0.3);
}

/* 暗色主题变量 */
:root {
  --tv-bg: #131722;
  --tv-panel: #1e222d;
  --tv-border: #2a2e39;
  --tv-text: #d1d4dc;
  --tv-text-muted: #787b86;
  --tv-primary: #2962ff;
  --tv-success: #089981;
  --tv-danger: #f23645;
}
</style>
